import { DateTime } from 'luxon';
import { Food, FoodHeader } from '@cf-shared/data-model/food';
import { isRecipe } from '@cf-shared/data-model/recipe';
import { MealScanFoodPick, MealScanPickOutcome } from '@cf-shared/scan-meal-photo-interface';
import { FormatNutrients } from 'util/formatting';
import { ConfigKey } from 'services/config-keys';
import { FoodReadFailurePolicy } from 'services/food-read-error';
import { SearchConnectionError } from 'services/food-search-error';
import { matchScannedFoodAsync } from 'services/scanned-food-match';
import { ScannerPreferenceMemory } from 'services/scanner-preference-memory';
import { PreparedStapleWords } from 'services/staple-db-snapshot';

/**
 * Turning one food name the AI returned into a database food, shared by the meal-photo scanner
 * and the recipe scanner.
 *
 * Both pages used to hold their own copy of everything in this file, and the copies were the
 * same code (deferred finding df-e0c7607513d0). Food staples plan 4c (#5625) had to add the same
 * import, the same constructor parameter, the same comment and the same four lines to both pages
 * verbatim — which is the cost this file removes: the two pages sit far apart in the tree and
 * nothing compared them, so a change made to only one copy would have drifted silently.
 *
 * It is deliberately NOT a base class. `design-docs/scanner-shared-components.md` ("Decision: no
 * ScannerPageBase") rejected inheritance for these pages because it would hide their genuinely
 * different analyze flows; a page calls this instead, the way it calls
 * `services/scanner-model-selection.ts` and the other shared scanner helpers.
 *
 * The matching RULES are not here. They live in `services/scanned-food-match.ts`, which is pure and
 * knows nothing about Angular services. This file is only the page-side assembly: which services
 * answer which search, and what the page does with a remembered pick.
 */

/**
 * What the shared match needs from a scanner page's injected services.
 *
 * Declared as the methods used rather than as the service classes, so a unit test can pass plain
 * objects and so this module cannot quietly start using something else on one of them.
 */
export interface ScannerFoodMatchServices {
  foodDb: {
    getFoodByIdAsync(id: string, opts: { cacheFood: boolean; onReadFailure: FoodReadFailurePolicy }): Promise<Food>;
    searchFoodByName_userAsync(text: string): Promise<FoodHeader[]>;
    searchFoodHeadersByName_elasticAsync(text: string, maxCount: number): Promise<FoodHeader[]>;
  };
  configDb: {
    getConfigValue<Type>(key: string): Type | undefined;
  };
  debug: {
    getNowLocal(): DateTime;
  };
  stapleDb: {
    getAllStapleHeadersAsync(): Promise<FoodHeader[]>;
    getPreparedWordsAsync(): Promise<Map<string, PreparedStapleWords>>;
  };
  logging: {
    trackSilentProblem(problemName: string, context: any): void;
    trackSilentInfo(infoName: string, context: any): void;
  };
}

/** One scanned food name to match, and the page's own answers to who does the looking. */
export interface ScannerFoodMatchRequest {
  /** the food name the AI returned, as it returned it */
  name: string;
  /** that name lowercased and trimmed — the key the page's remembered picks are stored under */
  normalizedName: string;
  /** the page's remembered picks: which database food the user chose for this AI name before */
  prefMemory: ScannerPreferenceMemory;
  /** the page's injected services */
  services: ScannerFoodMatchServices;
  /**
   * The Sentry name a food-server connection failure is filed under. It names the scanner, so a
   * meal scan's offline burst and a recipe scan's are counted apart.
   */
  connectionProblemName: string;
  /**
   * What the server's second AI call decided for this food (#5623). Only the meal scanner passes
   * it. Left out, or with an outcome this version does not know, the match is exactly what it was
   * before: the remembered pick, then the phone's own search and rule.
   */
  serverPick?: MealScanFoodPick;
}

/**
 * Whether a meal scan should ask the server to pick the database foods (#5623): only while the
 * MealScanAiPick key is an explicit `true`.
 */
export function mealScanAsksForServerPicks(configDb: ScannerFoodMatchServices['configDb']): boolean {
  return configDb.getConfigValue<boolean>(ConfigKey.MealScanAiPick) === true;
}

/**
 * The scanner result card's view of a database food.
 *
 * Kept here because the pages fill a card from a food in two places — the match below, and the
 * user picking a food by hand afterwards — and both pages did it identically.
 */
export function scannerFoodHeader(food: Food): FoodHeader {
  return {
    _id: food._id,
    name: food.name,
    displayNutrients: FormatNutrients(food.nutrients),
    recipe: isRecipe(food),
    status: food.status,
  };
}

/**
 * The database food for one AI-detected food name: the food the user picked before for it, else
 * the server's pick when the scan carried one (#5623), else the best search match, else null — in
 * which case the page shows the row unmatched with a Search button and the user picks by hand.
 *
 * `matchScannedFoodAsync` chooses the search match with the scanner pick rule, which searches like
 * the Foods page but picks by food words and source (#5590).
 */
export async function matchScannedFoodForScannerAsync(request: ScannerFoodMatchRequest): Promise<FoodHeader | null> {
  return (await matchScannedFoodWithSourceAsync(request)).header;
}

/**
 * Where a scanned row's food came from. The meal scanner sends it with its Mixpanel events, so a
 * food the user replaced can be counted by who chose it (#6656).
 */
export enum ScannedFoodMatchSource {
  /** the food this user picked for that name before */
  Remembered = 'Remembered',
  /** the server's second AI call picked it (#5623) */
  ServerPick = 'ServerPick',
  /** the server's call looked at the candidates and rejected them all, so the row is left empty */
  ServerNone = 'ServerNone',
  /** the phone's own search rule chose it */
  PhoneRule = 'PhoneRule',
  /** nothing was found, so the row is left empty */
  NoMatch = 'NoMatch',
  /** the user chose the food by hand on the scanner's result screen */
  UserChosen = 'UserChosen',
  /** the user added this food by hand because the scan missed it (#6807) */
  UserAdded = 'UserAdded',
}

/** A scanned food's match, and where it came from. */
export interface ScannedFoodMatch {
  header: FoodHeader | null;
  source: ScannedFoodMatchSource;
}

/** `matchScannedFoodForScannerAsync`, also saying where the food came from (#6656). */
export async function matchScannedFoodWithSourceAsync(request: ScannerFoodMatchRequest): Promise<ScannedFoodMatch> {
  const { name, normalizedName, prefMemory, services } = request;

  // if the user previously picked a DB food for this AI name
  const pref = prefMemory.get(normalizedName);
  if (pref) {
    const prefFood = await services.foodDb.getFoodByIdAsync(pref.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (prefFood) return { header: scannerFoodHeader(prefFood), source: ScannedFoodMatchSource.Remembered };
  }

  // then the server's own pick for this food, when the scan carried one (#5623)
  const serverPick = request.serverPick;
  // if the server's model looked at the candidates and rejected them all, the row stays unmatched
  // and the user picks by hand: filling it with the phone's rule would put back the very kind of
  // wrong pick the model declined to make
  if (serverPick?.outcome === MealScanPickOutcome.None) {
    return { header: null, source: ScannedFoodMatchSource.ServerNone };
  }
  if (serverPick?.outcome === MealScanPickOutcome.Picked && serverPick.foodId) {
    // NOT cached into the user's database, unlike the remembered pick above, which the user chose
    // themselves. A food stored there counts as "the user logged or created this" in the next
    // scan's ranking, on the server and on the phone, so caching a pick the user may still reject
    // would make a wrong pick more likely to be picked again. It is stored when the meal is saved,
    // as a food found by the phone's own rule is.
    const pickedFood = await services.foodDb.getFoodByIdAsync(serverPick.foodId, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (pickedFood) return { header: scannerFoodHeader(pickedFood), source: ScannedFoodMatchSource.ServerPick };
    // if the picked food cannot be loaded (deleted on this phone since the last sync, say), the
    // phone's own rule below still fills the row, so this is worth a note and not an alarm
    services.logging.trackSilentInfo('Scanner food match: server pick could not be loaded', { foodId: serverPick.foodId });
  }

  // then the device foods and the global search, ranked like the Foods page while the source
  // tiers are on (#5590); the rules live in scanned-food-match.ts
  // The curated staples this device holds, read before the match so the fallback inside it can
  // answer synchronously (food staples plan 4c, #5625). Both reads are answered from memory
  // once the snapshot is built, and both answer empty while it is not — in which case the scan
  // behaves exactly as it did before. The staples are only ever CONSULTED when the server
  // searches came back with nothing, so an online scan's pick is unchanged.
  const stapleHeaders = await services.stapleDb.getAllStapleHeadersAsync();
  const stapleWords = await services.stapleDb.getPreparedWordsAsync();
  const found = await matchScannedFoodAsync(name, {
    searchDevice: query => services.foodDb.searchFoodByName_userAsync(query),
    searchServer: (query, maxCount) => searchGlobalFoodsSafeAsync(request, query, maxCount),
    now: services.debug.getNowLocal().toUTC(),
    stapleFallbackHeaders: () => stapleHeaders,
    stapleWords,
  });
  return { header: found, source: found ? ScannedFoodMatchSource.PhoneRule : ScannedFoodMatchSource.NoMatch };
}

// one global Elasticsearch food search, made safe for the scanner matcher.
// A transient connectivity failure throws SearchConnectionError (#3743); for
// per-item matching that just means "no results for this query" — the scanner
// already surfaces unmatched items with a Search button — but leave a debugger
// signal so it isn't confused with a genuine no-match. trackSilentProblem
// groups by the message, so a whole-scan offline burst collapses to one issue.
async function searchGlobalFoodsSafeAsync(request: ScannerFoodMatchRequest, query: string, maxCount: number): Promise<FoodHeader[]> {
  try {
    return await request.services.foodDb.searchFoodHeadersByName_elasticAsync(query, maxCount);
  } catch (err) {
    if (err instanceof SearchConnectionError) {
      request.services.logging.trackSilentProblem(request.connectionProblemName, { foodName: query });
      return [];
    }
    throw err;
  }
}
